import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe, StripeError } from '@stripe/stripe-js';
import { Banner, Button, Card, Row, StateScreen } from '@cowoo/shared/ui-web';
import { ApiProblem, formatCardLabel, formatMoney, newIdempotencyKey, qk, STALE_TIME } from '@cowoo/shared';
import { needsEarlyPerformanceConsent } from '@cowoo/shared/checkout';
import { api } from '../../lib/api';
import { useCheckoutStore } from '../../lib/session';
import { stripeAppearance, stripeFonts, stripePromise } from '../../lib/stripe';

/**
 * `/zahlung` — quote, consents and payment (09-web-member § 8, 04 § 8.5).
 *
 * AF-06: on the web the order is created when the consents are ticked, because
 * the Payment Element needs a client secret. The order therefore exists before
 * the user has paid — and holds start running, which is why the countdown is
 * shown. The webhook stays the source of truth: after Stripe confirms, `/fertig`
 * polls the order until the server says `paid`.
 */

interface Payment {
  mode: 'payment' | 'setup' | 'none';
  clientSecret?: string;
}

export function Payment() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { items, idempotencyKey, orderId, setKey, setOrder } = useCheckoutStore();

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [earlyPerformance, setEarlyPerformance] = useState(false);
  const [problem, setProblem] = useState<ApiProblem | null>(null);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  /** § 8.3 — `trial_not_eligible`: the same order at the list price. */
  const [useTrial, setUseTrial] = useState(true);
  const [trialDialog, setTrialDialog] = useState(false);
  const [stripe, setStripe] = useState<Stripe | null>(null);

  const needsConsent = needsEarlyPerformanceConsent(items);
  const consented = acceptedTerms && (!needsConsent || earlyPerformance);

  useEffect(() => {
    void stripePromise().then(setStripe);
  }, []);

  const quote = useQuery({
    queryKey: qk.quote(`${JSON.stringify(items)}${useTrial ? '' : ':list-price'}`),
    enabled: items.length > 0,
    staleTime: 0,
    gcTime: 0,
    queryFn: async () => {
      const { data, error } = await api.POST('/orders/quote', { body: { items, useTrial } });
      if (error) throw error;
      return data;
    },
  });

  const method = useQuery({
    queryKey: qk.paymentMethod(),
    staleTime: STALE_TIME.me,
    queryFn: async () => {
      const { data } = await api.GET('/me/payment-method', {});
      return data ?? null;
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      const key = idempotencyKey ?? newIdempotencyKey();
      setKey(key);
      const { data, error } = await api.POST('/orders', {
        params: { header: { 'idempotency-key': key } },
        body: {
          items,
          acceptedTerms: true,
          ...(needsConsent ? { earlyPerformanceConsent: true as const } : {}),
          ...(useTrial ? {} : { useTrial: false }),
          channel: 'web' as const,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (!data) return;
      setProblem(null);
      setOrder(data.orderId);
      setPayment(data.payment);
      setExpiresAt(data.expiresAt);
    },
    onError: (error) => {
      if (!(error instanceof ApiProblem)) return setProblem(null);
      // § 8.3 — the refusals that leave this step.
      switch (error.code) {
        case 'desk_taken':
        case 'hold_expired':
          return navigate(`/platz?problem=${error.code}`, { replace: true });
        case 'already_member':
          return navigate('/mitglied', { replace: true });
        case 'billing_profile_required':
        case 'age_restricted':
          return navigate(`/angaben?problem=${error.code}`, { replace: true });
        case 'trial_not_eligible':
          return setTrialDialog(true);
        default:
          return setProblem(error);
      }
    },
  });

  // AF-06 — the consents create the order; un-ticking afterwards only disables the CTA.
  useEffect(() => {
    if (consented && !orderId && !create.isPending && quote.isSuccess) create.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consented, orderId, quote.isSuccess]);

  // A reload mid-checkout: the order exists, its payment details do not. The
  // same key returns the same order (04 § 8.5), so asking again is safe.
  useEffect(() => {
    if (orderId && !payment && consented && !create.isPending) create.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, payment, consented]);

  useEffect(() => {
    if (items.length === 0) navigate('/start', { replace: true });
  }, [items.length, navigate]);

  /** § 8.3 — leaving with a pending order releases the hold. */
  const leave = async (): Promise<void> => {
    if (orderId) await api.DELETE('/orders/{id}', { params: { path: { id: orderId } } });
    setOrder(null);
    setPayment(null);
    navigate('/platz');
  };

  if (quote.isLoading) return <StateScreen title={t('common.loading')} />;
  if (quote.isError) {
    const error = quote.error;
    return (
      <StateScreen
        title={t(error instanceof ApiProblem ? error.i18nKey : 'errors.unknown')}
        action={<Button onClick={() => navigate('/platz')}>{t('common.back')}</Button>}
      />
    );
  }

  const data = quote.data;
  if (!data) return null;

  const hasContract = items.some((item) => item.type === 'contract');
  const free = data.chargeCents === 0 && !data.trialApplied;
  const ctaLabel = data.trialApplied
    ? t('member.payment.submitFree', { amount: formatMoney(0) })
    : t('member.payment.submit', { amount: formatMoney(data.chargeCents) });
  const cardLabel = method.data ? formatCardLabel(method.data) : null;

  return (
    <main className="mx-auto max-w-2xl px-5 py-12">
      <p className="font-body text-muted mb-1 text-sm">{t('member.payment.hint')}</p>
      <h1 className="font-heading text-ink mb-6 text-3xl">{t('member.payment.title')}</h1>

      <Card className="mb-6">
        {data.lines.map((line, index) => (
          <Row
            key={`${line.productKey}-${index}`}
            label={
              line.proRata
                ? t('member.payment.proRata', {
                    description: line.description,
                    days: line.proRata.days,
                    daysInMonth: line.proRata.daysInMonth,
                  })
                : line.description
            }
            value={formatMoney(line.grossCents)}
          />
        ))}
        <Row label={t('member.payment.vat')} value={formatMoney(data.totalGrossCents - data.totalNetCents)} />
        {data.creditAppliedCents > 0 ? (
          <Row label={t('member.payment.credit')} value={`− ${formatMoney(data.creditAppliedCents)}`} />
        ) : null}
        <Row
          label={hasContract ? t('member.payment.dueToday') : t('member.payment.total')}
          value={formatMoney(data.chargeCents)}
        />
        {data.trialApplied ? (
          <p className="font-body text-muted pt-3 text-sm">{t('member.payment.trialNote')}</p>
        ) : null}
      </Card>

      {/* § 8.1 rule 4 — the consents sit above the payment area because they gate the order. */}
      <div className="mb-6 flex flex-col gap-3">
        <label className="font-body text-text flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            data-testid="consent-terms"
            checked={acceptedTerms}
            onChange={(event) => setAcceptedTerms(event.target.checked)}
          />
          {t('member.payment.acceptTerms')}
        </label>
        {needsConsent ? (
          // OI-L-02 — the wording is still with the lawyer; the requirement is not.
          <label className="font-body text-text flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              data-testid="consent-early"
              checked={earlyPerformance}
              onChange={(event) => setEarlyPerformance(event.target.checked)}
            />
            {t('member.payment.earlyPerformance')}
          </label>
        ) : null}
      </div>

      {expiresAt ? <HoldCountdown expiresAt={expiresAt} /> : null}

      {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}

      {!consented || !payment ? (
        <div
          className="border-line rounded-card font-body text-muted mb-6 border border-dashed p-6 text-sm"
          data-testid="payment-locked"
        >
          {create.isPending ? t('common.loading') : t('member.payment.consentFirst')}
        </div>
      ) : payment.mode === 'none' || free ? (
        <PaidByCredit orderId={orderId!} label={ctaLabel} />
      ) : stripe && payment.clientSecret ? (
        <Elements
          key={payment.clientSecret}
          stripe={stripe}
          options={{
            clientSecret: payment.clientSecret,
            locale: 'de',
            appearance: stripeAppearance,
            fonts: stripeFonts,
          }}
        >
          <PaymentForm
            orderId={orderId!}
            mode={payment.mode}
            clientSecret={payment.clientSecret}
            cardLabel={cardLabel}
            ctaLabel={ctaLabel}
            enabled={consented}
            hasContract={hasContract}
          />
        </Elements>
      ) : (
        <StateScreen title={t('common.loading')} />
      )}

      <button
        type="button"
        onClick={() => void leave()}
        className="text-teal-dark font-body mt-6 text-sm underline"
      >
        {t('member.payment.back')}
      </button>

      {trialDialog ? (
        <div
          role="dialog"
          aria-label={t('errors.trial_not_eligible')}
          className="bg-ink/40 fixed inset-0 z-50 flex items-center justify-center p-6"
        >
          <Card className="w-[420px] max-w-full">
            <p className="font-body text-ink mb-4">{t('errors.trial_not_eligible')}</p>
            <div className="flex flex-col gap-2">
              <Button
                full
                onClick={() => {
                  setTrialDialog(false);
                  setUseTrial(false);
                  // A different body is a different order: fresh key (04 § 8.5).
                  setKey(newIdempotencyKey());
                  setOrder(null);
                }}
              >
                {t('member.payment.forPrice', { price: formatMoney(data.totalGrossCents) })}
              </Button>
              <Button full variant="secondary" onClick={() => void leave()}>
                {t('common.back')}
              </Button>
            </div>
          </Card>
        </div>
      ) : null}
    </main>
  );
}

/** § 8.1 — the hold runs from the moment the order exists. */
function HoldCountdown({ expiresAt }: { expiresAt: string }) {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(() => Date.parse(expiresAt) - Date.now());
  useEffect(() => {
    const timer = setInterval(() => setRemaining(Date.parse(expiresAt) - Date.now()), 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);
  if (remaining <= 0) return null;
  const total = Math.floor(remaining / 1000);
  const countdown = `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  return (
    <p
      className={`font-body mb-4 inline-flex rounded-full px-3 py-1 text-sm font-semibold ${
        remaining < 120_000 ? 'bg-warn-bg text-warn-text' : 'bg-teal-tint text-teal-dark'
      }`}
      data-testid="hold-countdown"
    >
      {t('member.payment.hold', { countdown })}
    </p>
  );
}

/** Credit covered everything (or a 0 € voucher): no Stripe, the order is already settling. */
function PaidByCredit({ orderId, label }: { orderId: string; label: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-4">
      <p className="font-body text-text text-sm">{t('member.payment.paidWithCredit')}</p>
      <Button full onClick={() => navigate(`/fertig?order=${orderId}`)}>
        {label}
      </Button>
    </div>
  );
}

/**
 * The Element and the confirm (§ 8.1 rules 2 and 5). Lives inside `<Elements>`
 * so the hooks have their provider; the CTA is disabled until the Element
 * reports `complete`, or the saved card is chosen.
 */
function PaymentForm({
  orderId,
  mode,
  clientSecret,
  cardLabel,
  ctaLabel,
  enabled,
  hasContract,
}: {
  orderId: string;
  mode: 'payment' | 'setup';
  clientSecret: string;
  cardLabel: string | null;
  ctaLabel: string;
  enabled: boolean;
  hasContract: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [complete, setComplete] = useState(false);
  const [useSaved, setUseSaved] = useState(cardLabel !== null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const returnUrl = `${window.location.origin}/fertig?order=${orderId}`;

  const showStripeError = (error: StripeError): void => {
    // § 8.3 — Stripe's localised text is allowed for card problems only.
    setMessage(
      error.type === 'card_error' || error.type === 'validation_error'
        ? (error.message ?? t('errors.payment_failed'))
        : t('errors.payment_failed'),
    );
  };

  const confirmWithElement = async (): Promise<void> => {
    if (!stripe || !elements) return;
    setBusy(true);
    setMessage(null);
    const submitted = await elements.submit();
    if (submitted.error) {
      showStripeError(submitted.error);
      return setBusy(false);
    }
    const result =
      mode === 'setup'
        ? await stripe.confirmSetup({
            elements,
            clientSecret,
            confirmParams: { return_url: returnUrl },
            redirect: 'if_required',
          })
        : await stripe.confirmPayment({
            elements,
            clientSecret,
            confirmParams: { return_url: returnUrl },
            redirect: 'if_required',
          });
    if (result.error) {
      // A declined card leaves the order `pending` (server 07 § 4.5): correct
      // the card and confirm again with the same client secret.
      showStripeError(result.error);
      return setBusy(false);
    }
    navigate(`/fertig?order=${orderId}`);
  };

  const confirmSaved = async (): Promise<void> => {
    if (!stripe) return;
    setBusy(true);
    setMessage(null);
    const { data, error } = await api.POST('/orders/{id}/confirm-saved', {
      params: { path: { id: orderId }, header: { 'idempotency-key': newIdempotencyKey() } },
    });
    if (!error && data) return navigate(`/fertig?order=${orderId}`);
    const problem = error as { code?: string; clientSecret?: string } | undefined;
    if (problem?.code === 'payment_action_required' && problem.clientSecret) {
      const next = await stripe.handleNextAction({ clientSecret: problem.clientSecret });
      if (!next.error) return navigate(`/fertig?order=${orderId}`);
      showStripeError(next.error);
    } else {
      // § 8.3 — the saved card failed: say so and open the Element.
      setMessage(t('member.payment.savedFailed', { cardLabel: cardLabel ?? '' }));
      setUseSaved(false);
    }
    setBusy(false);
  };

  return (
    <div className="flex flex-col gap-4" data-testid="payment-area">
      {cardLabel ? (
        <div role="radiogroup" aria-label={t('member.payment.methodLabel')} className="flex flex-col gap-2">
          <label className="font-body text-text flex items-center gap-3 text-sm">
            <input
              type="radio"
              name="method"
              checked={useSaved}
              onChange={() => setUseSaved(true)}
              data-testid="method-saved"
            />
            {t('member.payment.useSaved', { cardLabel })}
          </label>
          <label className="font-body text-text flex items-center gap-3 text-sm">
            <input
              type="radio"
              name="method"
              checked={!useSaved}
              onChange={() => setUseSaved(false)}
              data-testid="method-other"
            />
            {t('member.payment.otherMethod')}
          </label>
        </div>
      ) : null}

      {!useSaved ? (
        <div className="border-line rounded-card border p-4" data-testid="payment-element">
          <PaymentElement
            options={{ layout: 'accordion', wallets: { applePay: 'auto', googlePay: 'auto' } }}
            onChange={(event) => setComplete(event.complete)}
          />
        </div>
      ) : null}

      {message ? (
        <p role="alert" className="font-body text-danger-text text-sm" data-testid="payment-error">
          {message}
        </p>
      ) : null}

      <Button
        full
        data-testid="pay-cta"
        disabled={!enabled || !stripe || (!useSaved && !complete)}
        loading={busy}
        onClick={() => void (useSaved ? confirmSaved() : confirmWithElement())}
      >
        {ctaLabel}
      </Button>
      <p className="font-body text-muted text-center text-xs">
        {hasContract ? t('member.payment.subContract') : t('member.payment.subOneOff')}
      </p>
    </div>
  );
}
